import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { distinctUntilChanged, map, skip } from 'rxjs/operators';
import { LegFiJwtService } from '../auth/legfi-jwt.service';

/** The organization the user is currently viewing. */
export interface CurrentOrg
{
    id: number;
    name: string;
}

/**
 * Canonical source of truth for "which organization is the user currently
 * viewing," plus a reactive notification when that changes.
 *
 * Today the value is FED by the JWT: `LegFiJwtService.store()` emits whenever the
 * stored org changes (org switch, background X-LegFiRefresh, demo enter/exit) and
 * this service re-reads the org from the token. That coupling lives entirely in
 * this service's constructor — consumers depend on `OrgContextService`, never on
 * `jwt.orgId` / `jwt.orgName` directly — so when org context eventually moves to
 * the URL (SD-3086) only the feed inside this service changes, not its consumers.
 *
 * Consumers: the Gable drawer pauses/resets its refresh on org change (SD-7898);
 * the org-context header labels recompute off it (SD-7870).
 */
@Injectable({
    providedIn: 'root',
})
export class OrgContextService implements OnDestroy
{
    private readonly _currentOrg$ = new BehaviorSubject<CurrentOrg | null>(
            OrgContextService._readOrgFromJwt(),
    );

    private readonly _feedSubscription: Subscription;

    constructor() {
        // FEED (SD-3086 seam): re-read the org from the JWT whenever store() reports the
        // org changed. store() has already written the new token when it emits, so the
        // read reflects the new context. This single subscription is the one place that
        // couples org context to the token — swap it for a URL-driven source later and
        // the consumers below are untouched.
        this._feedSubscription = LegFiJwtService.orgContextChanged$.subscribe(
                () => this._currentOrg$.next(OrgContextService._readOrgFromJwt()),
        );
    }

    ngOnDestroy(): void {
        this._feedSubscription.unsubscribe();
    }

    private static _readOrgFromJwt(): CurrentOrg | null {
        const jwt = LegFiJwtService.read();
        return jwt?.orgId ? { id: jwt.orgId, name: jwt.orgName } : null;
    }

    /** The org the user is currently viewing (null before authentication). */
    get currentOrg(): CurrentOrg | null {
        return this._currentOrg$.getValue();
    }

    /** Current org — replays the current value immediately, then every change. */
    get currentOrg$(): Observable<CurrentOrg | null> {
        return this._currentOrg$.asObservable().pipe(
                distinctUntilChanged((a, b) => a?.id === b?.id && a?.name === b?.name),
        );
    }

    /** Convenience accessor for just the current org id. */
    get currentOrgId(): number | null {
        return this._currentOrg$.getValue()?.id ?? null;
    }

    /** Current org id — replays the current value immediately, then every change. */
    get currentOrgId$(): Observable<number | null> {
        return this._currentOrg$.asObservable().pipe(
                map((org) => org?.id ?? null),
                distinctUntilChanged(),
        );
    }

    /**
     * Fires ONLY when the viewed org changes — the current value is skipped, so a
     * consumer subscribing at construction does not self-trigger. Subscribe here
     * to react to a *transition* (e.g. tear down and re-fetch for the new org).
     */
    get orgContextChanged$(): Observable<number | null> {
        return this.currentOrgId$.pipe(skip(1));
    }
}
